import { Form, Link, useSearchParams, useLoaderData } from "@remix-run/react";
import type { LoaderFunctionArgs } from "@remix-run/node";
import {
  AuthButton,
  AuthField,
  AuthInput,
  AuthNote,
  AuthPassword,
} from "~app/components/v13/auth";
import { NightLoginShell } from "~app/components/v13/login-night";
import { authClient } from "~app/lib/auth-client";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "@bprogress/remix";
import { ROUTES } from "~server/routes";
import { handleAuthLoader } from "~app/utils/auth-helpers";
import { getInvitationTypeFromUrl } from "~shared/constants";
import { InvitationType } from "~shared/types/invitation";
import { CLIENT_AMPLITUDE_EVENTS } from "~shared/constants/amplitude-events";
import { ampliClientTrack, initAmplitude } from "~app/lib/amplitude";
import { signInErrorMessage } from "~app/utils/auth-messages";

export const loader = async ({ request }: LoaderFunctionArgs) => {
  return handleAuthLoader({ request });
};

const loginSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(1, "Password is required"),
});

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { invitation, invitationError } = useLoaderData<typeof loader>();
  const invitationId = searchParams.get("invitationId");
  const typeParam = searchParams.get("type");

  // Determine invitation type from query param or URL
  const invitationType = invitationId
    ? (typeParam as InvitationType) ||
      (typeof window !== "undefined"
        ? getInvitationTypeFromUrl(window.location.href)
        : null) ||
      InvitationType.ORGANIZATION
    : null;

  const {
    formState: { errors, isSubmitting },
    setError,
    register,
    handleSubmit,
  } = useForm<z.infer<typeof loginSchema>>({
    defaultValues: {
      email: invitation?.email || "",
      password: "",
    },
    mode: "onTouched",
    reValidateMode: "onChange",
    resolver: zodResolver(loginSchema),
  });

  const signIn = async (data: z.infer<typeof loginSchema>) => {
    await authClient.signIn.email(
      {
        email: data.email,
        password: data.password,
      },
      {
        onSuccess: () => {
          initAmplitude();
          ampliClientTrack(CLIENT_AMPLITUDE_EVENTS.LOGIN_SUCCESS, {
            hasInvitation: Boolean(invitationId),
            invitationType: invitationType ?? undefined,
          });

          if (invitationId) {
            // Route to appropriate invitation acceptance page based on type
            if (invitationType === InvitationType.SUPER_ADMIN) {
              navigate(`/super-admin/accept-invitation/${invitationId}`);
            } else {
              navigate(`/accept-invitation/${invitationId}`);
            }
            return;
          }
          navigate(ROUTES.HOME);
        },
        onError: (error) => {
          // never the library's own wording: a wrong password and an unknown
          // address must read the same. The entered email stays in the form.
          setError("root", { message: signInErrorMessage(error.error) });
        },
      }
    );
  };
  return (
    /* ⭐ OPTION E, "OBSERVATORY NIGHT" (MC 68656b3c). The form below is the
       same form; only the page around it changed. There is no Microsoft
       sign-in configured, so the reference's "Continue with Microsoft" is not
       drawn: a button that cannot sign anyone in is not a design. */
    <NightLoginShell
      title={
        invitation?.email ? "Sign in to continue" : "Sign in to JumpCannon"
      }
      sub={
        invitation?.email
          ? `You were invited as ${invitation.email}. Sign in and we will finish accepting it.`
          : "Your book of business, classified and current."
      }
      foot={
        <div className="flex items-center justify-between">
          <Link
            prefetch="intent"
            to={ROUTES.FORGOT_PASSWORD}
            className="jc-night-link"
          >
            Forgot your password?
          </Link>
          {/* ⭐ Create an account is always offered (MC17430). Sign-up is open
              and creates a NORMAL user with no company access; an invitation,
              when there is one, still travels with the link so the invited
              company is joined by acceptance, never by signing up. */}
          <Link
            prefetch="intent"
            to={{
              pathname: ROUTES.REGISTER,
              search: invitationId
                ? `?invitationId=${invitationId}&type=${invitationType}`
                : "",
            }}
          >
            <span className="jc-night-link">New here? Create an account</span>
          </Link>
        </div>
      }
    >
      <Form onSubmit={handleSubmit(signIn)} noValidate>
        {errors.root ? <AuthNote>{errors.root.message}</AuthNote> : null}

        {invitationError ? (
          <AuthNote>
            {invitationError === "expired"
              ? "That invitation has expired. Ask whoever invited you to send a new one."
              : invitationError === "already_processed"
                ? "That invitation has already been used. Sign in below instead."
                : "That invitation is not valid."}
          </AuthNote>
        ) : null}

        <AuthField
          label="Email address"
          error={errors.email?.message ?? null}
          hint={invitation?.email ? "Filled in from your invitation." : null}
        >
          <AuthInput
            id="email"
            type="email"
            autoComplete="email"
            placeholder="you@company.com"
            invalid={Boolean(errors.email)}
            readOnly={!!invitation?.email}
            {...register("email")}
          />
        </AuthField>

        <AuthField label="Password" error={errors.password?.message ?? null}>
          <AuthPassword
            id="password"
            autoComplete="current-password"
            placeholder="Your password"
            invalid={Boolean(errors.password)}
            {...register("password")}
          />
        </AuthField>

        <AuthButton type="submit" disabled={isSubmitting}>
          {isSubmitting
            ? "Signing in…"
            : invitation?.email
              ? "Continue"
              : "Sign in"}
        </AuthButton>
      </Form>
    </NightLoginShell>
  );
}
